import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Preview';
import { analyzePortfolio } from '../../slices/portfolioSlice';

const REQUIRED_COLUMNS = ['ticker', 'shares'];
const ALLOWED_COLUMNS = [...REQUIRED_COLUMNS, 'cost_basis', 'purchase_date'];

export default function SpreadsheetUpload() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [processedData, setProcessedData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const userEmail = useSelector(state => state.auth.user?.email);

  const validateFile = (file) => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      return 'Please upload a valid Excel or CSV file';
    }
    if (file.size > maxSize) {
      return 'File size must be less than 5MB';
    }
    return '';
  };

  const validateSpreadsheetStructure = (headers) => {
    const normalizedHeaders = headers.map(h => h.toLowerCase());
    
    const missingColumns = REQUIRED_COLUMNS.filter(
      col => !normalizedHeaders.includes(col.toLowerCase())
    );

    if (missingColumns.length > 0) {
      throw new Error(
        `Missing required columns: ${missingColumns.join(', ')}`
      );
    }

    const unknownColumns = normalizedHeaders.filter(
      col => !ALLOWED_COLUMNS.includes(col.toLowerCase())
    );

    if (unknownColumns.length > 0) {
      throw new Error(
        `Unrecognized columns: ${unknownColumns.join(', ')}`
      );
    }
  };

  const validatePortfolioData = (data) => {
    const errors = [];
    const tickers = new Set();

    data.forEach((row, index) => {
      if (!row.ticker || !/^[A-Z]+$/.test(row.ticker)) {
        errors.push(`Row ${index + 1}: Invalid ticker symbol "${row.ticker}"`);
      }

      if (tickers.has(row.ticker)) {
        errors.push(`Row ${index + 1}: Duplicate ticker "${row.ticker}"`);
      }
      tickers.add(row.ticker);

      const shares = parseFloat(row.shares);
      if (isNaN(shares) || shares <= 0) {
        errors.push(`Row ${index + 1}: Invalid number of shares`);
      }
    });

    if (errors.length > 0) {
      throw new Error(errors.join('\n'));
    }
  };

  const processSpreadsheet = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          let data;
          if (file.name.endsWith('.csv')) {
            const text = e.target.result;
            const workbook = XLSX.read(text, { type: 'string' });
            data = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
          } else {
            const buffer = e.target.result;
            const workbook = XLSX.read(buffer, { type: 'array' });
            data = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
          }

          const headers = Object.keys(data[0] || {});
          validateSpreadsheetStructure(headers);

          const normalizedData = data.map(row => ({
            ticker: row.ticker.toUpperCase(),
            shares: parseFloat(row.shares),
            costBasis: row.cost_basis ? parseFloat(row.cost_basis) : undefined,
            purchaseDate: row.purchase_date || undefined
          }));

          validatePortfolioData(normalizedData);

          const portfolio = normalizedData.reduce((acc, row) => {
            acc[row.ticker] = row.shares;
            return acc;
          }, {});

          resolve({
            portfolio,
            rawData: normalizedData
          });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };

      const readMethod = file.name.endsWith('.csv') ? 'readAsText' : 'readAsArrayBuffer';
      reader[readMethod](file);
    });
  };

  const handleFile = async (fileObj) => {
    const validationError = validateFile(fileObj);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setFile(fileObj);

    try {
      const processed = await processSpreadsheet(fileObj);
      setProcessedData(processed);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, []);

  const handleFileSelect = (e) => {
    handleFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!processedData) {
      setError('No portfolio data to upload');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      await dispatch(analyzePortfolio({
        portfolio: processedData.portfolio,
        risk_free_rate: 0.05,
        lookback_years: 1
      })).unwrap();

      navigate(userEmail ? '/dashboard' : '/score/email');
    } catch (err) {
      setError(err.message || 'Error processing portfolio');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        sx={{
          p: 2,
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : file ? 'success.main' : 'divider',
          borderRadius: 2,
          backgroundColor: isDragging ? 'action.hover' : file ? 'success.lighter' : 'background.paper',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          minHeight: 50
        }}
      >
        <input
          type="file"
          id="fileUpload"
          hidden
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
        />
        
        <Box sx={{ p: 1 }}>
          {file ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 24 }} />
              <Typography variant="body2" color="success.main">
                {file.name}
              </Typography>
              <Button
                startIcon={<DeleteIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  setProcessedData(null);
                }}
                size="small"
                color="error"
              >
                Remove
              </Button>
              {processedData && (
                <Button
                  startIcon={<PreviewIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPreview(true);
                  }}
                  size="small"
                  color="primary"
                >
                  Preview
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <CloudUploadIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
              <Typography variant="body2">
                Drag and drop your spreadsheet here, or{' '}
                <Link
                  component="label"
                  htmlFor="fileUpload"
                  sx={{ cursor: 'pointer' }}
                >
                  browse
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Box sx={{ 
        mt: 2, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Link
          href="/template.xlsx"
          download
          variant="body2"
          sx={{ textDecoration: 'none' }}
        >
          Download template
        </Link>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isUploading || !processedData}
          size="small"
        >
          {isUploading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Analyze Portfolio'
          )}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      )}

      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Portfolio Preview</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticker</TableCell>
                  <TableCell align="right">Shares</TableCell>
                  <TableCell align="right">Cost Basis</TableCell>
                  <TableCell>Purchase Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processedData?.rawData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.ticker}</TableCell>
                    <TableCell align="right">{row.shares}</TableCell>
                    <TableCell align="right">
                      {row.costBasis ? `$${row.costBasis.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell>{row.purchaseDate || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}